import { useUpdateCameraPresence } from '@hakimo-ui/hakimo/data-access';
import { CameraDetail, PresenceRulesPayload } from '@hakimo-ui/hakimo/types';
import { toast, useCanUpdatePresenceZone } from '@hakimo-ui/hakimo/util';
import { Alert, Button, Toggle } from '@hakimo-ui/shared/ui-base';
import { useCallback } from 'react';

interface Props {
  camera: CameraDetail;
  config?: PresenceRulesPayload;
}

/**
 * Whether presence runs on this camera.
 *
 * The tenant setting is the default; the camera can override it either way.
 * `cameraEnabled` null means the camera has no setting of its own and the
 * toggle shows the tenant's; once it is set here, "Use tenant setting" clears
 * it again.
 */
export function PresenceSwitch({ camera, config }: Props) {
  const canUpdate = useCanUpdatePresenceZone();
  const mutation = useUpdateCameraPresence(camera.id, (payload) =>
    toast(
      payload.effectiveEnabled
        ? 'Presence switched on for this camera'
        : 'Presence switched off for this camera',
      { type: 'success' }
    )
  );

  const setEnabled = useCallback(
    (enabled: boolean | null) => mutation.mutate({ enabled }),
    [mutation]
  );

  if (!config) {
    return null;
  }

  const inherited = config.cameraEnabled == null;
  const tenantWord = config.enabled ? 'on' : 'off';

  return (
    <section className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg px-4 py-3">
      <div className="flex items-center gap-3">
        <Toggle
          enabled={config.effectiveEnabled}
          // Inert while a change is in flight: two overlapping PATCHes can
          // land out of order and leave the switch showing the older answer.
          onChange={
            canUpdate && !mutation.isLoading ? (v) => setEnabled(v) : undefined
          }
          title={config.effectiveEnabled ? 'On' : 'Off'}
        />
        <div className="min-w-0 flex-1">
          <p className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold">
            Presence on this camera
          </p>
          <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
            {inherited
              ? `Following the tenant setting (${tenantWord}).`
              : `Set on this camera; the tenant setting is ${tenantWord}.`}{' '}
            Takes a few minutes to apply.
          </p>
        </div>
        {canUpdate && !inherited && (
          <Button
            variant="outline"
            onClick={() => setEnabled(null)}
            disabled={mutation.isLoading}
          >
            Use tenant setting
          </Button>
        )}
      </div>
      {mutation.isError && (
        <Alert type="error">
          {mutation.error?.message ?? 'Could not update the presence switch'}
        </Alert>
      )}
    </section>
  );
}

export default PresenceSwitch;
